import React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import {
    VAULT77_HEALTH_DEFAULT_COLOR,
    VAULT77_HEALTH_ISSUE_COLORS,
    VAULT77_HEALTH_ISSUE_LABELS,
    type Vault77HealthIssue,
} from "../types";
import "./ReviewHealthBadge.css";

export interface ReviewHealthBadgeProps {
    /** The Vault77 health findings for this review (matched by FieldResponse review id). */
    issues: Vault77HealthIssue[];
}

function issueLabel(code: string): string {
    return VAULT77_HEALTH_ISSUE_LABELS[code] || code;
}

function issueColor(code: string): string {
    return VAULT77_HEALTH_ISSUE_COLORS[code] || VAULT77_HEALTH_DEFAULT_COLOR;
}

/**
 * Amber warning marker for a single review row on the Employee Reviews tab. Rendered only when the
 * Vault77 health scan flagged this review; hovering lists the issue(s), color-coded by type to match
 * the employee-row warning (HealthRowWarning) and the header gauges. Clicks are swallowed so reading
 * the popover doesn't also toggle the review accordion open.
 */
const ReviewHealthBadge: React.FC<ReviewHealthBadgeProps> = ({ issues }) => {
    if (!issues || issues.length === 0) return null;

    const popover = (
        <Popover id="vault77-review-health" className="ReviewHealthBadgePopover">
            <Popover.Title as="h3">
                {issues.length} data {issues.length === 1 ? "issue" : "issues"} on this review
            </Popover.Title>
            <Popover.Content>
                <ul className="ReviewHealthBadgePopover__list">
                    {issues.map((issue) => {
                        const color = issueColor(issue.Code);
                        return (
                            <li
                                key={`${issue.Code}-${issue.EmployeeReviewId ?? issue.PayRateHistoryId ?? issue.Message}`}
                                className="ReviewHealthBadgePopover__item"
                            >
                                <span className="ReviewHealthBadgePopover__label" style={{ color }}>
                                    <span
                                        className="ReviewHealthBadgePopover__dot"
                                        style={{ backgroundColor: color }}
                                    />
                                    {issueLabel(issue.Code)}
                                </span>
                                <span className="ReviewHealthBadgePopover__msg">{issue.Message}</span>
                            </li>
                        );
                    })}
                </ul>
            </Popover.Content>
        </Popover>
    );

    return (
        <OverlayTrigger trigger={["hover", "focus"]} placement="left" overlay={popover}>
            <span
                className="material-icons ReviewHealthBadge__icon"
                role="img"
                aria-label={`${issues.length} data ${issues.length === 1 ? "issue" : "issues"} on this review`}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
            >
                warning
            </span>
        </OverlayTrigger>
    );
};

export default ReviewHealthBadge;
